// Canvas v2 state. Lives on the root reactive store so prop functions
// re-evaluate on mutation. Writes funnel through `patchCanvas` so the
// backing store can later swap to project.settings.canvas without
// touching components.

import { resolveTopWindow, HANDLE_NAMES } from '../../globalScope.js'
// Bare shared-library import, not per-file deep functions/*.js subpaths — a
// per-file deep specifier into @symbo-ls/workspace-shared cannot resolve
// through the platform's public importmap (tickets/execution-board.md §1,
// same fix as workspace/app.js). Deferred call wrappers, not a module-scope
// destructure, per the a5d0a2aa barrel-cycle lesson.
import _wsShared from '@symbo-ls/workspace-shared'
import { isTrustedInstallMessage } from './installMessages.js'
const canvasResolveProjectParams = (...args) => _wsShared.functions.resolveProjectParams(...args)
const applyCanvasDotMetrics = (...args) => _wsShared.functions.applyCanvasDotMetrics(...args)
const canvasDotMetrics = (...args) => _wsShared.functions.canvasDotMetrics(...args)

// Built-in design-system pages shown on every project canvas — OUR DS showcase
// (DesignSystemScene) rendered with the USER's data, so users see their own
// design system. Item keys are `__ds/<route>`; CanvasItem routes these through
// embedDesignSystem. (2026-06-17 per Nika "show design system pages here on
// canvas … display users design system by our design system pages".)
const DS_PAGE_ROUTES = [
  '/colors',
  '/typography',
  '/spacing',
  '/icons',
  '/transitions',
  '/layout',
  '/misc'
]
export const DS_ITEM_KEYS = DS_PAGE_ROUTES.map((r) => '__ds' + r)
export const isDsItemKey = (k) => typeof k === 'string' && k.indexOf('__ds/') === 0

// Entity-node key shape (`ref:<type>:<id>`). The full contract lives in
// `@symbo-ls/workspace-shared/functions/entityRef.js` (`parseEntityRef` /
// `isEntityRef`); this is the cheap PREFIX test the hot paths in this file
// need (the chrome refresh runs it per item per frame), and it is inlined
// here rather than reached through `el.call` because itemVisibleUnderFilter
// is a plain module function with no element to call on.
//
// It is deliberately LOOSER than `isEntityRef`: this one only has to decide
// whether the chrome should treat a key as a data card, and a malformed
// `ref:` key must still be routed to the card that can show its error state
// rather than falling through to the artboard branch and mounting an iframe.
export const isEntityItemKey = (k) => typeof k === 'string' && k.indexOf('ref:') === 0

export function shouldShowDesignSystem(root) {
  // OFF by default (Nika 2026-08-01) — the design-system frames only seed onto
  // the board when the user explicitly toggles them on (toggleCanvasDesignSystem).
  // The board defaults to just the project's pages; DS + components are opt-in.
  // Read the LIVE flag first (root.canvas, set by a toggle) so a toggle takes
  // effect immediately; fall back to the persisted flag (root.data.canvas) at
  // init before root.canvas is populated. Absent anywhere ⇒ off.
  const live = root?.canvas?.showDesignSystem
  if (typeof live === 'boolean') return live
  // Persisted flag is only authoritative when the doc was saved under the new
  // default-off logic — detected by the presence of `showComponents` (which only
  // the new persist writes). OLD docs auto-persisted showDesignSystem:true for
  // everyone (the pre-2026-08-01 `!== false` default), so treat those as OFF —
  // otherwise every existing board would ignore the new default. A real user
  // toggle re-saves with showComponents present, and is then honoured.
  const c = root?.data?.canvas
  if (c && typeof c.showComponents === 'boolean') return c.showDesignSystem === true
  return false
}

export function shouldShowComponents(root) {
  // OFF by default (Nika 2026-08-01) — component frames are opt-in, same as DS.
  const live = root?.canvas?.showComponents
  if (typeof live === 'boolean') return live
  return root?.data?.canvas?.showComponents === true
}

// A NEW board page's default name, in the session language: the canvas
// bundle's `canvas.boardPage.defaultName` ("Page {n}" / "გვერდი {n}") with the
// page's number. Called ONLY where a page is minted — addBoardPage, and the
// first page of a board that has none (the fresh seed, a doc saved before board
// pages or from a template, the pre-init patch, the persist fallback). A saved
// name is data and never comes back through here: a board saved with "Page 1"
// keeps "Page 1" in a Georgian session. `el` is any element of the app (polyglot
// reads the root `lang`); with no polyglot the name is the number, never empty.
const boardPageDefaultName = (el, n) => {
  const words = el?.call?.('polyglot', 'canvas.boardPage.defaultName')
  return typeof words === 'string' && words.includes('{n}')
    ? words.replace('{n}', String(n))
    : String(n)
}

// `firstPageName` (boardPageDefaultName(el, 1)) names the board's first page.
// Every caller that STORES the result passes it; a caller that reads one field
// (`DEFAULTS().viewport`, `.guides`) does not.
const DEFAULTS = (firstPageName) => ({
  viewport: { x: 40, y: 40, scale: 0.35 },
  items: {},
  groups: {},
  guides: { horizontal: [], vertical: [] },
  selection: { ids: [], primary: null },
  marquee: null,
  mode: 'default',
  snap: {
    enabled: true,
    threshold: 6, // screen px tolerance
    toGrid: true, // grid snap ON by default
    gridStep: 64, // canvas-space px
    toItems: true,
    toGuides: true
  },
  // Per-tile iframes are the only render path now: native window/document
  // isolation per artboard, so scratch's FACTORY, designSystem, and state
  // tree never bleed between tiles or into the canvas chrome. The earlier
  // same-document inline mount was dropped — same-document mounting can't
  // honestly isolate window globals or document-level mutations. The
  // `frameMode` toggle that used to gate between the two paths is gone.
  // Visual connections between items — arrows from one item to another.
  // Stored as `[{ from: key, to: key, label?: string, kind?: string }]`.
  // Rendered by `refreshCanvasChrome` into the `CvConnectors` SVG layer.
  // `kind` is OPTIONAL and additive (entity nodes, spec §3.3): a connection
  // without one renders exactly as it always did — same stroke, no label.
  connections: [],
  // Entity-node cache — `{ [refKey]: { loading, data, error } }` (spec §3.1).
  // Deliberately NOT in `_livePageSlice` and NOT in persistCanvasLayout's
  // payload: it is a per-tab READ CACHE of rows the backend owns, so it must
  // survive a board-page switch (patchCanvas merges, so it does) and must
  // never be written into the project document (which would freeze a stale
  // copy of somebody else's record into the canvas layout).
  entities: {},
  // Off by default. Toggled from the settings modal.
  showRulers: false,
  showMinimap: false,
  settingsOpen: false,
  session: null,
  // Figma-style board PAGES (Nika 2026-08-01). The ACTIVE page's layout always
  // lives in the top-level live fields above (items/guides/groups/viewport/
  // connections) so CanvasSurface / chrome / drag stay page-agnostic; inactive
  // pages are parked as snapshots in `pageItems[id]`. A board always has ≥1 page.
  pages: [{ id: 'page-1', name: firstPageName }],
  activePageId: 'page-1',
  pageItems: {}
})

function rootState(el) {
  return el?.state?.root || el?.getRootState?.() || el?.getRoot?.()?.state
}

const CANVAS_STORAGE_KEY = 'cv2_settings'
function loadStored() {
  try {
    return JSON.parse(window.localStorage?.getItem(CANVAS_STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}
function persistSettings(canvas) {
  try {
    const slim = {
      snap: canvas.snap,
      showRulers: canvas.showRulers,
      showMinimap: canvas.showMinimap
    }
    window.localStorage?.setItem(CANVAS_STORAGE_KEY, JSON.stringify(slim))
  } catch {}
}

export function patchCanvas(patch, opts) {
  const root = rootState(this)
  if (!root?.update) return
  const prev = root.canvas || DEFAULTS(boardPageDefaultName(this, 1))
  const next = { ...prev, ...patch }
  root.update({ canvas: next }, opts)
  if ('snap' in patch || 'showRulers' in patch || 'showMinimap' in patch) persistSettings(next)
  // refreshCanvasChromeNow only computes the minimap's content while
  // showMinimap is true (perf gate — see there), so flipping it off→on needs
  // one explicit refresh here or the panel would stay blank until the next
  // drag/pan/selection change happened to run one.
  if (patch.showMinimap === true && !prev.showMinimap) this.call('refreshCanvasChrome')
}

// Single helper for snap field toggles — replaces the inline
// `{...v2.snap, X: y}` spread that 6 settings handlers were doing.
export function patchSnap(patch) {
  const v2 = this.call('getCanvasV2')
  if (!v2) return
  this.call('patchCanvas', { snap: { ...v2.snap, ...patch } })
}

// Direct mutation for high-frequency commits (drag/resize/nudge). A
// state.update here would rebuild every CanvasItem and remount any
// iframe children; the style was already written imperatively by the
// gesture handler.
export function patchCanvasItem(key, itemPatch) {
  const root = rootState(this)
  const canvas = root?.canvas
  if (!canvas || !key) return
  canvas.items = canvas.items || {}
  canvas.items[key] = { ...(canvas.items[key] || {}), ...itemPatch }
}

// Debounced project write. The `canvas` field is a recognized
// contentField — the server stores it in the ProjectData document and
// includes it in subsequent project reads. `settings` is also a
// contentField but many project reads strip it, so we keep the layout
// at the top level to match Workspace's `canvasPosition` pattern.
//
// The pending write is `{ projectId, timer, write }`: the latest layout of ONE
// project's board. The debounce folds that project's writes only. A write
// still pending for ANOTHER project (the board switched inside the 800 ms) is
// that board's last edit: it is sent at once, never cancelled. Cancelling it
// lost the edit whenever the next board saved first.
let _persistTimer = null
export function persistCanvasLayout() {
  const v2 = this.call('getCanvasV2')
  if (!v2) return
  const ctx = this.context || this.getRootContext?.() || {}
  const sdk = ctx.methods?.sdk
  if (!sdk?.isReady?.()) return
  const el = this
  const root = rootState(this)
  const data = root?.data
  const projectId = data?._id
  if (!projectId) return
  // Board pages: the top-level items/guides/… ARE the active page's layout
  // (kept for backward-compat + the loader's persisted.items read); pageItems
  // holds every page's snapshot with the active one refreshed from the live
  // fields. (Nika 2026-08-01)
  const activePageId = v2.activePageId || 'page-1'
  const pages =
    Array.isArray(v2.pages) && v2.pages.length
      ? v2.pages
      : [{ id: activePageId, name: boardPageDefaultName(this, 1) }]
  const pageItems = { ...(v2.pageItems || {}), [activePageId]: _livePageSlice(v2) }
  const payload = {
    items: v2.items,
    guides: v2.guides,
    groups: v2.groups,
    viewport: v2.viewport,
    connections: v2.connections,
    pages,
    activePageId,
    pageItems,
    // Both default OFF now — persist the explicit flag (=== true) so an
    // untoggled board stays off across reloads instead of the old !== false
    // (which defaulted an undefined flag back ON). (Nika 2026-08-01)
    showDesignSystem: v2.showDesignSystem === true,
    showComponents: v2.showComponents === true
  }

  const write = async () => {
    // Mark this as a LOCAL write so the remote-update watcher doesn't surface the
    // version it creates as an external/AI edit (functions/canvas/remoteUpdates.js).
    // Stamp BEFORE the write — the version event can arrive before this resolves.
    try {
      el.call('markSelfWrite', root)
    } catch (_) {}
    try {
      await sdk.updateProject(projectId, { canvas: payload })
    } catch (err) {
      console.warn('[canvas v2] persist failed', err?.message || err)
    }
  }
  const pending = _persistTimer
  if (pending) clearTimeout(pending.timer)
  if (pending && pending.projectId !== projectId) pending.write()
  const next = { projectId, write, timer: null }
  next.timer = setTimeout(() => {
    if (_persistTimer === next) _persistTimer = null
    write()
  }, 800)
  _persistTimer = next
}

export function getCanvasV2() {
  return rootState(this)?.canvas
}

// Which drag model the studios board is using — a PURE read of root state, so
// it answers everywhere, not only while that board is mounted.
//
// It lived in pages/studios.js and was installed onto `context.functions` on
// /studio mount and `delete`d on unmount, alongside four genuinely board-
// coupled helpers (layoutPositions / persistPositions / redrawConnections /
// refreshIntegrations). Those four drive this module's live board state and
// MUST stay absent off-studio. This one never touched it — it was grouped with
// them by proximity. SettingsModalBody mounts from three places and two of them
// (the shell's settings layer, the tickets kanban) never mount studios, so
// `el.call('getDragMode')` THREW there. Measured on the served UI-kit catalog,
// /canvas page 2 (2026-09-20). Its call sites already carry a `|| 'grid'`
// fallback that could never run, because an unregistered `el.call` throws
// before its result is used — the fallback is real now.
//
// Home chosen so ONE export reaches both registries: this file is re-exported
// by `functions/index.js` AND by `functions/eagerSlice.js`, which is the slice
// the shell merges. Pinned by canvas/__tests__/dragModeRegistration.test.js.
export function getDragMode(root) {
  return root?.dragMode || 'grid'
}

export function isCanvasV2() {
  // The flag's canonical home is the canvas globalScope (globalScope.js —
  // "this scope only carries the flag because it's read by both editor
  // chrome and iframe"). Reading it ONLY through the Canvas element
  // (`getCanvasScope()` = getCanvas()?.scope) made the answer depend on
  // mount ORDER: the `if:` gates on Canvas's own children (CanvasSurface vs
  // the legacy CanvasScene) evaluate while Canvas is still being created,
  // before `getCanvas()` can find it — so in the unified shell the v2 gate
  // read `undefined?.canvasV2` → false, mounted the LEGACY empty gridstack
  // scene, and never re-evaluated (the gate has zero reactive reads). Fall
  // back to context.globalScope, which exists from the first evaluation.
  const scope = this.call('getCanvasScope')
  if (scope && 'canvasV2' in scope) return !!scope.canvasV2
  return !!this.context?.globalScope?.canvasV2
}

export function getCanvasItem(key) {
  return this.call('getCanvasV2')?.items?.[key]
}

export function getCanvasSelection() {
  return this.call('getCanvasV2')?.selection || { ids: [], primary: null }
}

export function getCanvasMode() {
  return this.call('getCanvasV2')?.mode || 'default'
}

export function setCanvasV2Mode(mode) {
  const v2 = this.call('getCanvasV2')
  if (!v2) return
  v2.mode = mode
  const scope = this.call('getCanvasScope')
  if (scope) {
    scope.defaultMode = mode === 'default'
    scope.inspectMode = mode === 'inspect'
    scope.panningMode = mode === 'pan'
  }
  // Re-render the modes bar + inspect overlay (uses reactive `show:`).
  this.call('patchCanvas', { mode })
  this.call('refreshCanvasChrome')
}

export function toggleCanvasSettings() {
  const v2 = this.call('getCanvasV2')
  if (!v2) return
  this.call('patchCanvas', { settingsOpen: !v2.settingsOpen })
}

export function setCanvasSettings(open) {
  this.call('patchCanvas', { settingsOpen: !!open })
}

// Viewport mutates in place — the scene transform is driven imperatively
// via `applyViewport`, so reactive re-render is unnecessary.
export function setViewport(patch) {
  const canvas = rootState(this)?.canvas
  if (!canvas) return
  canvas.viewport = { ...(canvas.viewport || {}), ...patch }
}

// Canonical "apply a viewport change": persist + scene transform +
// grid-bg refresh + mirror to v1 channels + reposition screen-space
// chrome (selection, handles, marquee, navbar, guides, snap lines).
export function applyViewport(viewport) {
  this.call('setViewport', viewport)
  const scope = this.call('getCanvasScope')
  if (scope)
    scope.positions = { ...scope.positions, x: viewport.x, y: viewport.y, scale: viewport.scale }
  try {
    resolveTopWindow().__cvViewport = { x: viewport.x, y: viewport.y, scale: viewport.scale }
  } catch {}
  const root = this.getRoot?.()
  // Per-tick camera writes go STRAIGHT to the nodes — a full DOMQL
  // el.update() cycle per pointer-move tick (×2: scene + grid) was the
  // single biggest per-frame cost of pan/zoom (2026-08-08 per Nika "make
  // canvas zooming panning more smooth - it feels heavy now"). Writing
  // node.style still mutates the style ATTRIBUTE, so the viewport-windowing
  // MutationObserver contract (observes CvScene's style attr) is untouched.
  const scene = root?.lookdown?.('CvScene')
  if (scene?.node) {
    scene.node.style.transform = `translate3d(${viewport.x}px, ${viewport.y}px, 0) scale(${viewport.scale})`
  } else if (scene?.update) {
    scene.update({
      style: {
        transform: `translate3d(${viewport.x}px, ${viewport.y}px, 0) scale(${viewport.scale})`
      }
    })
  }
  const grid = root?.lookdown?.('CvGrid')
  if (grid?.node) {
    // Spacing + dot radius together (canvasBackground.canvasDotMetrics): the
    // recursive fold keeps spacing in a bounded band, and the radius tracks it
    // so ink density is CONSTANT at every zoom. Radius used to be pinned while
    // spacing swept 3x, which faded the grid out and popped it back mid-gesture
    // — "sometimes during scroll dots disappear" (2026-08-08 per Nika).
    applyCanvasDotMetrics(grid.node, viewport.scale, viewport.x, viewport.y)
  } else if (grid?.update) {
    // No node yet (element declared but not mounted) — spacing/position only;
    // the radius keeps its CSS fallback until the node exists and the branch
    // above takes over.
    const { spacing } = canvasDotMetrics(viewport.scale)
    const px = ((viewport.x % spacing) + spacing) % spacing
    const py = ((viewport.y % spacing) + spacing) % spacing
    grid.update({
      style: {
        backgroundSize: `${spacing}px ${spacing}px`,
        backgroundPosition: `${px}px ${py}px`
      }
    })
  }
  this.call('refreshCanvasChrome')
  // The `this.call('syncCanvas')` that used to sit here (pure overhead on
  // the camera's hottest path — every pointermove during pan/pinch, every
  // coalesced wheel flush) is gone along with the rest of the dead
  // `syncCanvas` machinery — see `methods/index.js` for the full removal
  // note (tickets/opus.md, 2026-08-09 investigation: zero reads repo-wide,
  // all other call sites audited and either already re-rendered via
  // `refreshCanvasChrome`/`.update()` or had a real reactive replacement).
}

// --- coord conversions --------------------------------------------------
// Canvas ↔ screen. Screen coords are relative to the canvas iframe's
// viewport (document window). CanvasEditor shares the same doc, so
// using `window.innerWidth/innerHeight` for center math is correct.
export function canvasToScreen(cx, cy) {
  const vp = this.call('getCanvasV2')?.viewport || { x: 0, y: 0, scale: 1 }
  return { x: cx * vp.scale + vp.x, y: cy * vp.scale + vp.y }
}

export function screenToCanvas(sx, sy) {
  const vp = this.call('getCanvasV2')?.viewport || { x: 0, y: 0, scale: 1 }
  return { x: (sx - vp.x) / vp.scale, y: (sy - vp.y) / vp.scale }
}

// --- chrome updater -----------------------------------------------------
// rAF-coalesces: callers tag the chrome dirty; the actual layout work
// runs once per browser frame even if called 100×/frame during gesture.
// Cache last applied geom per element so unchanged frames skip the
// `el.update` (a no-op write still allocates DOMQL diff overhead).

let _chromeRafId = null
let _chromeLastEl = null
// The app root captured at SCHEDULE time. The scheduling element can unmount
// before the deferred frame runs — e.g. an AiContextChip's RemoveBtn dispatches
// toggleSelection, setSel bumps `_cvSelectionTick`, the chip list re-renders
// and that very button is removed (owned state destroyed, parent nulled).
// refreshCanvasChromeNow then saw `getRoot()` → undefined and returned early,
// leaving the selection box + handles painted over an EMPTY selection — and the
// stale box ate every later pointerdown on the artboard (AI-CONTEXT-AWARENESS-
// O1-1, live 2026-08-21). Run against this root instead when that happens.
let _chromeLastRoot = null
const _lastGeom = new WeakMap()

// SelectedNavbar host positioning (below) needs the live rail width to avoid
// drawing underneath it. `root.lookdown('ProjectCanvasSidebar')` (a tree walk)
// + `node.getBoundingClientRect()` (a forced synchronous layout read) both used
// to run unconditionally on EVERY refreshCanvasChrome tick — i.e. every
// pointermove while dragging/resizing an item, the highest-frequency path in
// this file (up to 60×/sec) — sandwiched between the selection-box/handle
// style WRITES just above and the host style WRITE just below. That write →
// forced-read → write order is textbook layout thrash: the browser can't
// answer getBoundingClientRect from the already-stale layout after the writes
// above, so it forces a synchronous reflow, which the writes below
// immediately invalidate again. The rail is a single stable element for a
// canvas page's whole lifetime and its width is DS-token-pinned (G1), so
// cache both the element lookup and the measured width; a ResizeObserver
// invalidates the cached width on the rare occasions it actually changes
// (viewport resize, the page sidebar's hide / show — the hidden rail shrinks
// to its reopen tab) instead of re-measuring on every frame. The rail's SIDE
// is read per pass, not cached: the page-sidebar rule (shared
// functions/pageSidebar.js) puts it on the right beside a left navbar, and a
// move between the sides keeps its width.
// One `const` holder mutated in place, not four `let` bindings — reassigned
// module-scope `let`s can't ride frank's globalScope lift, so at a
// frank-served runtime `_sidebarWidthFor` read four guaranteed-undefined
// identifiers (gate: unresolvable-identifier, surfaced through the
// workspace-shell merge). A const object lifts as data and property
// mutation keeps the cache semantics identical.
const _sidebarWidthState = { el: null, width: 0, node: null, ro: null }

function _sidebarWidthFor(root) {
  let sidebarEl = _sidebarWidthState.el
  if (!sidebarEl?.node?.isConnected) {
    sidebarEl = root?.lookdown?.('ProjectCanvasSidebar') || null
    _sidebarWidthState.el = sidebarEl
  }
  const node = sidebarEl?.node
  if (!node) return 0
  if (node !== _sidebarWidthState.node) {
    _sidebarWidthState.node = node
    _sidebarWidthState.width = node.getBoundingClientRect().width || 0
    _sidebarWidthState.ro?.disconnect()
    if (typeof ResizeObserver !== 'undefined') {
      _sidebarWidthState.ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect?.width
        if (typeof w === 'number') _sidebarWidthState.width = w
      })
      _sidebarWidthState.ro.observe(node)
    }
  }
  return _sidebarWidthState.width
}

// CANVAS-ARTBOARD-OFFSET-1 — the ONE shared container-origin term (analytics
// root cause, 2026-08-18): every chrome writer below emits `board*scale + vp`
// as overlay-local left/top, but the ARTBOARDS render inside the
// CanvasSurface container, whose top-left is NOT the overlay's origin on the
// shell's project-canvas page (the shell band offsets it). Pointer input
// (`toCanvas`, pointer.js) already subtracts the surface rect, so hits were
// correct while every chrome box (selection, handles, titles, groups,
// connectors, marquee, snap lines, guides) sat shifted by exactly that
// CONSTANT — zoom never touches it. The origin is measured RELATIVE TO THE
// OVERLAY's own rect (surfaceRect − overlayRect): identical to the absolute
// surface origin when the overlay is at the viewport origin (the standalone
// canvas app — term 0, nothing changes) and still correct if the overlay's
// fixed containing block is itself offset by a transformed ancestor. Element
// lookups cached in a const holder (same frank-lift reasoning as
// _sidebarWidthState above); rects are read ONCE per refresh/write pass —
// callers pass the result down, never re-measure per handle.
const _chromeOriginState = { surfEl: null }

function chromeOriginFor(root, overlay) {
  let surfEl = _chromeOriginState.surfEl
  if (!surfEl?.node?.isConnected) {
    surfEl = root?.lookdown?.('CanvasSurface') || null
    _chromeOriginState.surfEl = surfEl
  }
  const surfNode = surfEl?.node
  const overNode = overlay?.node
  if (!surfNode?.getBoundingClientRect || !overNode?.getBoundingClientRect) {
    return { x: 0, y: 0 }
  }
  const sRect = surfNode.getBoundingClientRect()
  const oRect = overNode.getBoundingClientRect()
  // `w`: the overlay's own width, for a clamp against its right edge.
  return { x: sRect.left - oRect.left, y: sRect.top - oRect.top, w: oRect.width }
}

function geomChanged(el, next) {
  const prev = _lastGeom.get(el)
  if (!prev) {
    _lastGeom.set(el, next)
    return true
  }
  for (const k in next)
    if (next[k] !== prev[k]) {
      _lastGeom.set(el, next)
      return true
    }
  return false
}

// rAF RACED against a 100ms timeout — the same hidden-tab-proof scheduler as
// CanvasSurface's scheduleWindowRecompute, for the same reason: on a hidden/
// occluded tab Chrome stops firing rAF entirely, so the old rAF-only defer
// left `_chromeRafId` truthy forever and every later refresh returned at the
// debounce gate — selection box/handles/SelectedNavbar frozen at display:none
// for the whole hidden lifetime (bit the 2026-08-08 SelectedNavbar
// verification exactly as it bit windowing). Whichever side fires first runs
// the refresh and cancels the other; a token-owned slot makes stale callbacks
// no-ops.
export function refreshCanvasChrome() {
  _chromeLastEl = this
  const rootNow = this?.getRoot?.()
  if (rootNow) _chromeLastRoot = rootNow
  if (_chromeRafId) return
  const pending = {}
  const run = () => {
    if (_chromeRafId !== pending) return
    _chromeRafId = null
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(pending.raf)
    clearTimeout(pending.timer)
    try {
      // A detached scheduler (see _chromeLastRoot) can no longer resolve the
      // root; the captured root element is a valid `this` for the refresh —
      // getRoot() is itself, state.root is the store, call() has the graph.
      const target = _chromeLastEl?.getRoot?.() ? _chromeLastEl : _chromeLastRoot
      if (!target) return
      refreshCanvasChromeNow.call(target)
    } catch (err) {
      console.error('[canvas v2] refreshCanvasChrome error', err)
    }
  }
  _chromeRafId = pending
  pending.raf =
    typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame(run) : setTimeout(run, 16)
  pending.timer = setTimeout(run, 100)
}

// Is an item shown under the active ProjectCanvasSidebar filter? Mirrors the
// CanvasSurface CvItems filter so the chrome (titles / selection box) hides for
// filtered-out items too. (2026-06-17 per Nika "when filtering canvas, its
// related UI should also disappear".)
function itemVisibleUnderFilter(rs, key) {
  const filter = rs?._canvasFilter || 'all'
  if (filter === 'all') return true
  // Entity nodes (`ref:<type>:<id>`) are exempt from every type filter. The
  // filter buckets are PROJECT-SCHEMA buckets — pages, components, design
  // system — and a data item is none of them, so bucketing it
  // as 'other' would make any non-'all' filter blank a board whose content is
  // entirely entity nodes. They belong to the BOARD PAGE, which is already the
  // only membership that scopes them (`canvas.items` holds the active page's
  // set; parked pages live in `pageItems`).
  if (isEntityItemKey(key)) return true
  if (filter === 'ds') return isDsItemKey(key)
  if (filter === 'pages') return key in (rs?.data?.pages || {})
  if (filter === 'components') return key in (rs?.data?.components || {})
  return true
}

// ── Connection stroke by relation KIND (spec §3.3) ─────────────────────────
// The SVG connector layer is drawn imperatively (it is one <path> per
// connection recomputed per frame, which no declarative form can express
// cheaply), so the per-kind styling has to be plain attribute writes too.
//
// Raw rgba here, not design tokens, for the SAME reason every other value in
// this renderer is raw: these attributes are written onto SVG nodes with
// setAttribute, which never resolves a token, and the whole connector layer
// already paints canvas-tool chrome over arbitrary user content at a fixed
// contrast (see the `ruler()` note in CanvasOverlayV2). The two accents mirror
// the existing default so the family reads as one.
//
// A connection with NO `kind` takes the default branch and is byte-identical
// to what shipped before entity nodes existed.
const _CONN_DASHED = ['supersedes', 'amends']
const _CONN_ACCENT = ['because_of', 'variation_of']
export function canvasConnectionStroke(kind) {
  if (kind && _CONN_ACCENT.indexOf(kind) !== -1) return 'rgba(255,159,64,0.9)'
  if (kind && _CONN_DASHED.indexOf(kind) !== -1) return 'rgba(180,142,255,0.9)'
  return 'rgba(64,156,255,0.85)'
}
export function canvasConnectionDash(kind) {
  return kind && _CONN_DASHED.indexOf(kind) !== -1 ? '6 4' : null
}

function refreshCanvasChromeNow() {
  const v2 = this.call('getCanvasV2')
  if (!v2) return
  const root = this.getRoot?.()
  if (!root) return
  const overlay = root.lookdown?.('CanvasOverlayV2')
  if (!overlay) return
  const el = this
  // Guard against an empty/partial viewport (e.g. a persisted `{}`) — a missing
  // scale would make every screen-space position NaN and the chrome would float
  // off the items / vanish. Fall back to the canonical default transform.
  const vp = v2.viewport && v2.viewport.scale != null ? v2.viewport : { x: 40, y: 40, scale: 0.35 }
  const rs = rootState(this)
  // ONE container-origin read per refresh pass (see chromeOriginFor above).
  const origin = chromeOriginFor(root, overlay)

  // Selection box + handles — only when at least one selected item is visible
  // under the current filter (a filtered-out item is unrendered, so its box +
  // handles + SelectedNavbar must hide too).
  const box = overlay.CvSelectionBox
  const ids = v2.selection.ids.filter((id) => itemVisibleUnderFilter(rs, id))
  if (ids.length && box?.update) {
    const bbox = el.call('bboxForSelection')
    if (bbox) {
      const x = bbox.x * vp.scale + vp.x + origin.x
      const y = bbox.y * vp.scale + vp.y + origin.y
      const w = bbox.w * vp.scale
      const h = bbox.h * vp.scale
      box.update({
        style: {
          display: 'block',
          left: x + 'px',
          top: y + 'px',
          width: w + 'px',
          height: h + 'px'
        }
      })

      // Handles — only for single selection
      const showHandles = ids.length === 1
      const handlePoints = [
        [HANDLE_NAMES[0], x, y],
        [HANDLE_NAMES[1], x + w / 2, y],
        [HANDLE_NAMES[2], x + w, y],
        [HANDLE_NAMES[3], x + w, y + h / 2],
        [HANDLE_NAMES[4], x + w, y + h],
        [HANDLE_NAMES[5], x + w / 2, y + h],
        [HANDLE_NAMES[6], x, y + h],
        [HANDLE_NAMES[7], x, y + h / 2]
      ]
      for (const [name, hx, hy] of handlePoints) {
        const h = overlay[name]
        if (!h?.update) continue
        const next = { display: showHandles ? 'block' : 'none', left: hx, top: hy }
        if (!geomChanged(h, next)) continue
        h.update({
          style: {
            display: next.display,
            left: hx + 'px',
            top: hy + 'px',
            transform: 'translate(-50%, -50%)'
          }
        })
      }

      // SelectedNavbar host — only when exactly 1 selected
      const host = overlay.CvSelectedHost
      if (host?.update) {
        const sidebarWidth = _sidebarWidthFor(root)
        // Clear the rail on ITS side: a rail on the right (beside a left
        // navbar) holds the host's right edge back, as a left rail holds its
        // left edge.
        const rail = _sidebarWidthState.el
        const railRight =
          !!rail &&
          typeof rail.call === 'function' &&
          (rail.call('pageSidebarRead', rs, rail) || {}).side === 'right'
        const safeLeft = railRight
          ? origin.w > 0
            ? Math.min(x, origin.w - sidebarWidth - 24 - w)
            : x
          : Math.max(x, sidebarWidth + 24)
        host.update({
          style: showHandles
            ? {
                display: 'block',
                left: safeLeft + 'px',
                top: y + 'px',
                width: w + 'px',
                height: h + 'px',
                zIndex: '2'
              }
            : { display: 'none' }
        })
      }
    }
  } else {
    box?.update?.({ style: { display: 'none' } })
    for (const name of HANDLE_NAMES) overlay[name]?.update?.({ style: { display: 'none' } })
    overlay.CvSelectedHost?.update?.({ style: { display: 'none' } })
  }

  // Marquee
  const marq = overlay.CvDragAreaClip?.CvMarquee || overlay.CvMarquee
  if (marq?.update) {
    const m = v2.marquee
    if (m) {
      marq.update({
        style: {
          display: 'block',
          left: m.x * vp.scale + vp.x + origin.x + 'px',
          top: m.y * vp.scale + vp.y + origin.y + 'px',
          width: m.w * vp.scale + 'px',
          height: m.h * vp.scale + 'px'
        }
      })
    } else {
      marq.update({ style: { display: 'none' } })
    }
  }

  // Connector arrows — render as bezier paths in CvConnectors SVG.
  const conn = overlay.CvConnectors
  const connNode = conn?.node
  if (connNode) {
    const list = v2.connections || []
    const dom = connNode.ownerDocument
    while (connNode.firstChild) connNode.removeChild(connNode.firstChild)
    if (list.length) {
      // Marker for arrowhead.
      const defs = dom.createElementNS('http://www.w3.org/2000/svg', 'defs')
      defs.innerHTML =
        '<marker id="cvArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="rgba(64,156,255,0.9)"/></marker>'
      connNode.appendChild(defs)
      for (const c of list) {
        const a = v2.items[c.from]
        const b = v2.items[c.to]
        if (!a || !b) continue
        const ax = (a.x + a.w) * vp.scale + vp.x + origin.x
        const ay = (a.y + a.h / 2) * vp.scale + vp.y + origin.y
        const bx = b.x * vp.scale + vp.x + origin.x
        const by = (b.y + b.h / 2) * vp.scale + vp.y + origin.y
        const cx = (ax + bx) / 2
        const path = dom.createElementNS('http://www.w3.org/2000/svg', 'path')
        path.setAttribute('d', `M${ax},${ay} C${cx},${ay} ${cx},${by} ${bx},${by}`)
        path.setAttribute('fill', 'none')
        path.setAttribute('stroke', canvasConnectionStroke(c.kind))
        path.setAttribute('stroke-width', '2')
        const dash = canvasConnectionDash(c.kind)
        if (dash) path.setAttribute('stroke-dasharray', dash)
        path.setAttribute('marker-end', 'url(#cvArrow)')
        connNode.appendChild(path)
        // Relation label at the bezier MIDPOINT. A cubic with these two
        // control points has its t=0.5 point at (ax+bx)/2 horizontally and the
        // average of the two endpoint heights vertically — the closed form,
        // not a sampled approximation, so the label cannot drift off the curve
        // at any zoom. Only drawn when the connection carries a `label`, which
        // is what keeps page/component connections pixel-identical to before.
        if (!c.label) continue
        const label = dom.createElementNS('http://www.w3.org/2000/svg', 'text')
        label.setAttribute('x', String((ax + bx) / 2))
        label.setAttribute('y', String((ay + by) / 2 - 4))
        label.setAttribute('text-anchor', 'middle')
        label.setAttribute('fill', canvasConnectionStroke(c.kind))
        label.setAttribute('font-size', '11')
        label.setAttribute('paint-order', 'stroke')
        label.setAttribute('stroke', 'rgba(0,0,0,0.55)')
        label.setAttribute('stroke-width', '3')
        label.setAttribute('stroke-linejoin', 'round')
        label.textContent = String(c.label)
        connNode.appendChild(label)
      }
    }
  }

  // Group dashed outlines — one screen-space rect per group.
  const groupsHost = overlay.CvGroupsLayer
  const groupsNode = groupsHost?.node
  if (groupsNode) {
    const groups = v2.groups || {}
    const existing = new Map()
    Array.from(groupsNode.children).forEach((c) => {
      const k = c.getAttribute('data-group-for')
      if (k) existing.set(k, c)
    })
    const seen = new Set()
    for (const [gid, g] of Object.entries(groups)) {
      seen.add(gid)
      const bbox = el.call('bboxForGroup', gid)
      if (!bbox) continue
      let box = existing.get(gid)
      if (!box) {
        box = groupsNode.ownerDocument.createElement('div')
        box.setAttribute('data-group-for', gid)
        box.style.position = 'absolute'
        box.style.border = '1px dashed rgba(64,156,255,0.85)'
        box.style.borderRadius = '4px'
        box.style.pointerEvents = 'none'
        groupsNode.appendChild(box)
      }
      const sx = bbox.x * vp.scale + vp.x + origin.x
      const sy = bbox.y * vp.scale + vp.y + origin.y
      box.style.left = sx - 4 + 'px'
      box.style.top = sy - 4 + 'px'
      box.style.width = bbox.w * vp.scale + 8 + 'px'
      box.style.height = bbox.h * vp.scale + 8 + 'px'
    }
    for (const [gid, box] of existing) if (!seen.has(gid)) box.remove()
  }

  // Minimap — render items + viewport rect in a 180x120 box. Gate on
  // `showMinimap` (off by default) before doing ANY of this: `show:`/`hide:`
  // in this framework only ever toggle CSS `display` (see
  // smbls/packages/element/src/create.js — the CSS_PROPS_REGISTRY branch for
  // `show`/`hide` writes `node.style.display`, it never removes the node), so
  // `mmNode` stays truthy the whole time the minimap panel is merely hidden.
  // Without this gate, EVERY refresh — up to 60×/sec during any drag, resize,
  // pan or marquee, whether or not the minimap was ever opened — paid for a
  // forced-synchronous-layout `getBoundingClientRect()` read plus a full
  // Object.values(v2.items) bbox scan plus a full per-item dot create/update
  // pass, for a panel that's off in the overwhelming majority of sessions.
  const minimap = overlay.CvMinimap
  const mmNode = minimap?.node
  if (mmNode && v2.showMinimap) {
    const surf = root.lookdown?.('CanvasSurface')?.node
    const rect = surf?.getBoundingClientRect()
    let mmMinX = Infinity,
      mmMinY = Infinity,
      mmMaxX = -Infinity,
      mmMaxY = -Infinity
    for (const it of Object.values(v2.items)) {
      if (it.x < mmMinX) mmMinX = it.x
      if (it.y < mmMinY) mmMinY = it.y
      if (it.x + it.w > mmMaxX) mmMaxX = it.x + it.w
      if (it.y + it.h > mmMaxY) mmMaxY = it.y + it.h
    }
    if (Number.isFinite(mmMinX) && rect) {
      const PAD = 8
      const W = mmNode.clientWidth - PAD * 2
      const H = mmNode.clientHeight - PAD * 2
      const cw = mmMaxX - mmMinX
      const ch = mmMaxY - mmMinY
      const mScale = Math.min(W / cw, H / ch)
      const ox = PAD - mmMinX * mScale
      const oy = PAD - mmMinY * mScale
      const dom = mmNode.ownerDocument
      const have = new Map()
      Array.from(mmNode.children).forEach((c) => {
        const k = c.getAttribute('data-mm-for')
        if (k) have.set(k, c)
      })
      const seen = new Set()
      for (const [key, it] of Object.entries(v2.items)) {
        seen.add(key)
        let dot = have.get(key)
        if (!dot) {
          dot = dom.createElement('div')
          dot.setAttribute('data-mm-for', key)
          dot.style.position = 'absolute'
          dot.style.background = 'rgba(255,255,255,0.6)'
          dot.style.borderRadius = '1px'
          mmNode.appendChild(dot)
        }
        dot.style.left = it.x * mScale + ox + 'px'
        dot.style.top = it.y * mScale + oy + 'px'
        dot.style.width = it.w * mScale + 'px'
        dot.style.height = it.h * mScale + 'px'
      }
      for (const [key, dot] of have) if (!seen.has(key) && key !== '__viewport__') dot.remove()

      // Viewport rect in canvas-space: [-vp.x/scale, -vp.y/scale] sized [rect.w/scale, rect.h/scale]
      let vpEl = have.get('__viewport__')
      if (!vpEl) {
        vpEl = dom.createElement('div')
        vpEl.setAttribute('data-mm-for', '__viewport__')
        vpEl.style.position = 'absolute'
        vpEl.style.border = '1px solid rgba(64,156,255,0.95)'
        vpEl.style.background = 'rgba(64,156,255,0.15)'
        vpEl.style.pointerEvents = 'none'
        mmNode.appendChild(vpEl)
      }
      const vx = -vp.x / vp.scale
      const vy = -vp.y / vp.scale
      const vw = rect.width / vp.scale
      const vh = rect.height / vp.scale
      vpEl.style.left = vx * mScale + ox + 'px'
      vpEl.style.top = vy * mScale + oy + 'px'
      vpEl.style.width = vw * mScale + 'px'
      vpEl.style.height = vh * mScale + 'px'
    }
  }

  // Item titles — one screen-space label per item, positioned just above.
  const titlesHost = overlay.CvItemTitles
  const titlesNode = titlesHost?.node
  if (titlesNode) {
    const existing = new Map()
    Array.from(titlesNode.children).forEach((c) => {
      const k = c.getAttribute('data-title-for')
      if (k) existing.set(k, c)
    })
    const seen = new Set()
    for (const [key, it] of Object.entries(v2.items)) {
      // Filtered-out items aren't rendered → drop their title too (left out of
      // `seen`, so the cleanup pass below removes any stale label).
      if (!itemVisibleUnderFilter(rs, key)) continue
      seen.add(key)
      let lbl = existing.get(key)
      if (!lbl) {
        lbl = titlesNode.ownerDocument.createElement('div')
        lbl.setAttribute('data-title-for', key)
        lbl.style.position = 'absolute'
        lbl.style.color = 'white'
        lbl.style.fontSize = '11px'
        lbl.style.fontWeight = '500'
        lbl.style.padding = '2px 6px'
        lbl.style.background = 'rgba(0,0,0,0.6)'
        lbl.style.borderRadius = '3px'
        lbl.style.transform = 'translateY(-100%)'
        lbl.style.marginTop = '-4px'
        lbl.style.pointerEvents = 'none'
        lbl.style.whiteSpace = 'nowrap'
        // Friendly label for built-in design-system frames (`__ds/colors` →
        // "Design system · Colors") instead of the raw key.
        lbl.textContent = isDsItemKey(key)
          ? 'Design system · ' + key.slice(5).replace(/^\w/, (c) => c.toUpperCase())
          : key
        titlesNode.appendChild(lbl)
      }
      const sx = it.x * vp.scale + vp.x + origin.x
      const sy = it.y * vp.scale + vp.y + origin.y
      lbl.style.left = sx + 'px'
      lbl.style.top = sy + 'px'
    }
    // Remove labels for items that no longer exist.
    for (const [key, lbl] of existing) {
      if (!seen.has(key)) lbl.remove()
    }
  }
}

export function resetCanvasView() {
  this.call('applyViewport', { x: 40, y: 40, scale: 0.35 })
}

// Zoom by `factor` around the CanvasSurface centre — the single camera-zoom
// math for every outer-chrome entry point (methods zoomIn/zoomOut, the
// panzoom-shaped shim's smoothZoom, the Zoombar presets' zoomToScale). Same
// clamp as the shim (0.035–2.5). No-op without a v2 board or a measured
// surface (keeps a stale bridge from a torn-down surface harmless).
export function zoomAroundCenter(factor) {
  if (!Number.isFinite(factor) || factor <= 0) return
  const v2 = this.call('getCanvasV2')
  if (!v2?.viewport) return
  const surf = this.getRoot?.()?.lookdown?.('CanvasSurface')
  const rect = surf?.node?.getBoundingClientRect?.()
  if (!rect || !rect.width || !rect.height) return
  const cx = rect.width / 2
  const cy = rect.height / 2
  const sv = v2.viewport
  const newScale = Math.max(0.035, Math.min(2.5, sv.scale * factor))
  const worldX = (cx - sv.x) / sv.scale
  const worldY = (cy - sv.y) / sv.scale
  this.call('applyViewport', {
    x: cx - worldX * newScale,
    y: cy - worldY * newScale,
    scale: newScale
  })
}

// Absolute zoom: land on `scale` exactly, anchored on the surface centre.
export function zoomToScale(scale) {
  if (!Number.isFinite(scale) || scale <= 0) return
  const v2 = this.call('getCanvasV2')
  if (!v2?.viewport?.scale) return
  this.call('zoomAroundCenter', scale / v2.viewport.scale)
}

// ZOOMBAR-PRESET-DROPDOWN-DEAD-1 — the v2 camera bridge behind the
// panzoom-shaped shim in globalScope.js. Before this, the shim's mutators
// (moveTo/moveBy/zoomAbs/smoothZoom/smoothZoomAbs) only WROTE
// `__cvViewport` — a mirror that applyViewport overwrites and nobody reads
// back — so every v1-shaped zoom from outer chrome (the shared Zoombar's +/-
// and presets, the Box dblclick 100%, CommandBar `/zoom`) moved the Zoombar
// label and NOTHING on the board (live: CvScene stayed at scale 0.4199 while
// the shim and label read 1.5). The shim now routes through this object when
// it is installed, so those calls land in the SAME camera pipeline the
// wheel/pan use (applyViewport: state + scene transform + chrome + mirrors).
// Installed from initCanvasV2 (every CanvasSurface mount, before its
// pre-load guard) and bound to that element only through `this.call`, so a
// surface torn down by a route switch leaves a bridge whose every call
// no-ops (zoomAroundCenter finds no CanvasSurface) — never a stale write.
export function installV2CameraBridge() {
  const self = this
  const tw = resolveTopWindow()
  if (!tw) return
  tw.__cvCamera = {
    zoomBy: (factor) => self.call('zoomAroundCenter', factor),
    zoomTo: (scale) => self.call('zoomToScale', scale),
    moveTo: (x, y) => {
      const v2 = self.call('getCanvasV2')
      if (!v2?.viewport) return
      self.call('applyViewport', { ...v2.viewport, x, y })
    },
    moveBy: (dx, dy) => {
      const v2 = self.call('getCanvasV2')
      if (!v2?.viewport) return
      self.call('applyViewport', {
        ...v2.viewport,
        x: v2.viewport.x + dx,
        y: v2.viewport.y + dy
      })
    }
  }
}

// Pure bbox reducer over a canvas-v2 `items` map — same shape/loop as
// fitToSelection's "no selection ⇒ fit everything" fallback below, factored
// out so the never-arranged-project zoom-to-fit path (initCanvasV2's
// fresh-seed branch) can compute a bbox without a live v2 state object.
// Returns `null` for an empty map — callers decide the NaN/Infinity-safe
// fallback camera, this never does.
export function bboxForItems(items) {
  const values = Object.values(items || {})
  if (!values.length) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const it of values) {
    if (it.x < minX) minX = it.x
    if (it.y < minY) minY = it.y
    if (it.x + it.w > maxX) maxX = it.x + it.w
    if (it.y + it.h > maxY) maxY = it.y + it.h
  }
  return Number.isFinite(minX) &&
    Number.isFinite(minY) &&
    Number.isFinite(maxX) &&
    Number.isFinite(maxY)
    ? { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
    : null
}

// Pure camera-fit computation — no DOM, no `this`, unit-testable in
// isolation. Given a content bbox (canvas-space) and the viewport pixel
// size (CanvasSurface's rect), returns the {x, y, scale} camera that frames
// the bbox with `pad` px of breathing room on every side.
//
// Clamps chosen for the "never-arranged project opens zoomed to fit" default
// (tickets/nika.md "PRODUCT CALL", 2026-08-16):
//   - maxScale 1   — a single sparse tile must never zoom in PAST 100%; the
//     tile is a fixed 1722px-square thumbnail, so >1x is upscaling, not
//     framing, and looks soft on a large/4K CanvasSurface.
//   - minScale 0.02 — an absolute floor only, not a target. The whole point
//     of fitting a many-tile board (bellforge-app: 23 tiles) is a bird's-eye
//     overview at whatever scale that takes — deliberately small is correct,
//     this floor only stops a pathological board (hundreds of tiles)
//     producing a scale that rounds to 0.
// Any bbox/rect that can't produce a finite positive scale returns
// `fallback` untouched — the caller's existing default camera — rather than
// ever emitting NaN/Infinity/0.
export function computeFitToContentViewport(bbox, rect, opts = {}) {
  const {
    pad = 120,
    minScale = 0.02,
    maxScale = 1,
    fallback = { x: 40, y: 40, scale: 0.35 }
  } = opts
  if (!bbox || !Number.isFinite(bbox.w) || !Number.isFinite(bbox.h) || bbox.w <= 0 || bbox.h <= 0) {
    return fallback
  }
  if (
    !rect ||
    !Number.isFinite(rect.width) ||
    !Number.isFinite(rect.height) ||
    rect.width <= 0 ||
    rect.height <= 0
  ) {
    return fallback
  }
  const sx = (rect.width - 2 * pad) / bbox.w
  const sy = (rect.height - 2 * pad) / bbox.h
  const rawScale = Math.min(sx, sy)
  if (!Number.isFinite(rawScale) || rawScale <= 0) return fallback
  const scale = Math.max(minScale, Math.min(maxScale, rawScale))
  const x = (rect.width - bbox.w * scale) / 2 - bbox.x * scale
  const y = (rect.height - bbox.h * scale) / 2 - bbox.y * scale
  return { x, y, scale }
}

export function fitToSelection() {
  const v2 = this.call('getCanvasV2')
  if (!v2) return
  const ids = v2.selection.ids
  const bbox = ids.length
    ? this.call('bboxForSelection')
    : (() => {
        const all = Object.keys(v2.items)
        if (!all.length) return null
        return (
          this.call('bboxForGroup', '__all__') ||
          (() => {
            let minX = Infinity,
              minY = Infinity,
              maxX = -Infinity,
              maxY = -Infinity
            for (const it of Object.values(v2.items)) {
              if (it.x < minX) minX = it.x
              if (it.y < minY) minY = it.y
              if (it.x + it.w > maxX) maxX = it.x + it.w
              if (it.y + it.h > maxY) maxY = it.y + it.h
            }
            return Number.isFinite(minX)
              ? { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
              : null
          })()
        )
      })()
  if (!bbox) return
  const root = this.getRoot?.()
  const surf = root?.lookdown?.('CanvasSurface')
  const rect = surf?.node?.getBoundingClientRect()
  if (!rect) return
  const PAD = 60
  const sx = (rect.width - 2 * PAD) / bbox.w
  const sy = (rect.height - 2 * PAD) / bbox.h
  const scale = Math.max(0.05, Math.min(2, Math.min(sx, sy)))
  const x = (rect.width - bbox.w * scale) / 2 - bbox.x * scale
  const y = (rect.height - bbox.h * scale) / 2 - bbox.y * scale
  this.call('applyViewport', { x, y, scale })
}

// Zoom-to-fit for a NEVER-ARRANGED project (tickets/nika.md "PRODUCT CALL",
// 2026-08-16 — Nika: a project with no saved canvas arrangement opens
// zoomed to fit its content instead of the hardcoded 0.35 default).
// Deliberately separate from fitToSelection: that function fits the current
// SELECTION (or, with none, falls back to fitting everything) and is reused
// by keyboard shortcuts / persisted-board-restore focus — its clamp (0.05–2)
// serves that different use case. This one is only ever called once, right
// after initCanvasV2 seeds a fresh grid, fits ALL items unconditionally, and
// uses its own tighter clamp (see computeFitToContentViewport). A no-op
// (leaves whatever camera is already painted) if there's no CanvasSurface
// rect yet or no items to fit — initCanvasV2 already seeded a safe fallback
// camera synchronously, so doing nothing here is never a blank board.
export function fitCanvasToContent() {
  const v2 = this.call('getCanvasV2')
  if (!v2) return
  const bbox = this.call('bboxForItems', v2.items)
  if (!bbox) return
  const root = this.getRoot?.()
  const surf = root?.lookdown?.('CanvasSurface')
  const rect = surf?.node?.getBoundingClientRect()
  if (!rect) return
  const viewport = this.call('computeFitToContentViewport', bbox, rect, {
    fallback: v2.viewport || DEFAULTS().viewport
  })
  this.call('applyViewport', viewport)
}

// `kind` is optional and additive — omit it and the connection renders exactly
// as before (plain accent stroke, no label). See refreshCanvasChromeNow.
export function addConnection(from, to, label, kind) {
  const v2 = this.call('getCanvasV2')
  if (!v2 || !from || !to) return
  const next = [...(v2.connections || []), kind ? { from, to, label, kind } : { from, to, label }]
  this.call('patchCanvas', { connections: next })
  this.call('refreshCanvasChrome')
  this.call('persistCanvasLayout')
}

export function removeConnection(from, to) {
  const v2 = this.call('getCanvasV2')
  if (!v2) return
  const next = (v2.connections || []).filter((c) => !(c.from === from && c.to === to))
  this.call('patchCanvas', { connections: next })
  this.call('refreshCanvasChrome')
  this.call('persistCanvasLayout')
}

export function toggleShortcutsOverlay() {
  const v2 = this.call('getCanvasV2')
  if (!v2) return
  this.call('patchCanvas', { shortcutsOpen: !v2.shortcutsOpen })
}

// The eager panzoom shim lives in globalScope.js. It reads from
// `topWindow.__cvViewport`, which `applyViewport` writes on every
// viewport change — no second installation needed here.

// Per-tab-session "already seeded" memory for the previewSize seed below —
// keyed by project IDENTITY (owner/key: a bare key is not unique across
// owners), not by whether `root.canvas.items` happened to be empty (a
// template that also pre-seeds real canvas.items, e.g. mobile, never takes
// the items-empty branch, so that flag can't double as this guard). Module
// scope, not component scope: it must survive the SPA re-mounting
// CanvasSurface on every project navigation within one tab, and reset only
// on a real page load (a fresh module instance).
// The device-preview breakpoint a project type opens at. Only `mobile` forces
// one — app/service/deck/library keep the unforced default. `mobileXS` is the
// named 375x768 entry in DeviceButtonSet's own size vocabulary
// (shared/components/DeviceButtonSet.js); never a raw width here.
const PREVIEW_SIZE_BY_PROJECT_TYPE = { mobile: 'mobileXS' }

const previewSizeSeededProjects = new Set()

export function initCanvasV2() {
  const root = rootState(this)
  // Camera bridge first — it must exist even while the pre-load guard below
  // keeps bailing, so outer-chrome zoom works from the first painted frame.
  // Direct same-module invocation, not `this.call`: the guard's contract is
  // "touch nothing" (newProjectPicker.test pins zero registry calls on a
  // bail), and the bridge writes only the top-window hook, never state.
  installV2CameraBridge.call(this)

  // Pre-load guard — CanvasSurface.onRender calls this synchronously while
  // `main` mounts, which races `ensureProjectLoaded`'s async fetch: at that
  // instant `context.projectPages` is still `{}` and `root.data` is either
  // unset (first project opened this session) or stale (a previously-viewed
  // project). Seeding from either produces bogus items — the premature call
  // sees 0 pages + 0 components, which doesn't match the "minimal blank"
  // shape, so `shouldShowDesignSystem` defaults to true and seeds the 7
  // built-in `__ds/*` placeholder tiles into `root.canvas.items`.
  // `ensureProjectLoaded` then re-invokes this function once real data
  // lands, but by then `root.canvas.items` is already non-empty, so THIS
  // premature seed — not the real one — wins: the reconcile branch below
  // deletes the placeholder DS tiles and re-adds the real pages/components,
  // but positions them via a free-slot scan corrupted by the placeholders'
  // stale grid occupancy, and never re-fits the viewport. For a brand-new
  // "blank" project (exactly one `home` component) the result is a single
  // tile stranded far outside the default viewport — a blank screen. Bail
  // out here (without touching `root.canvas` at all) until `root.data`
  // actually matches the URL's project; the seed still runs exactly once,
  // from real data, when `ensureProjectLoaded`'s follow-up call lands.
  const { owner: urlOwner, key: urlKey } = canvasResolveProjectParams()
  if (urlOwner && urlKey) {
    const loadedKey = root?.data?.key || root?.data?.projectKey
    if (loadedKey !== urlKey) return
  }

  // The board belongs to its project. root.canvas lives on the SHELL root, and
  // a project switch keeps it: navigation.js swaps root.data in place and calls
  // this again. After A -> B, root.canvas still held A's board, the board-pages
  // guard below took it for B's own, reconciled B's pages INTO it and persisted
  // it to B's id (persistCanvasLayout writes root.data._id): B's saved layout
  // never loaded and was overwritten with A's. Where a board is restored or
  // seeded below, it carries its project's id (`projectId`; never persisted —
  // persistCanvasLayout writes named fields only). A board of another project
  // is no board here: every branch below reads `board`, so B restores or seeds
  // its own, and that write replaces root.canvas as a whole. (history.js keeps
  // its undo stack per board the same way.)
  const board = root?.canvas && root.canvas.projectId === root?.data?._id ? root.canvas : null

  // Idempotency guard (Nika 2026-08-01, board pages). CanvasSurface.onRender
  // fires initCanvasV2 on EVERY render, so once the board is initialised (has
  // pages) this must NOT restore-from-persisted or re-seed — an empty `items`
  // is no longer "uninitialised", it's a legitimately-empty new board page, and
  // reseeding would clobber a just-added page (reverting pages/activePageId).
  // Reconcile missing project pages only onto a NON-empty active page (never
  // auto-fill an intentional blank page), then return.
  // Paint the restored camera BEFORE any early return. The persisted viewport
  // is restored into STATE, but nothing wrote it to the DOM: CvScene keeps its
  // declared placeholder transform `translate3d(40px, 40px, 0) scale(0.35)`
  // until applyViewport runs, and every early return below skipped it — the
  // board-pages guard immediately after this is the one real boards actually
  // take. Result: a board saved at any other camera painted every tile at the
  // wrong place. Measured 2026-08-08 on natali/shop — state camera
  // {x: 702, y: -6192, scale: 0.2308} vs a scene still on 40/40/0.35, putting
  // all six mounted tiles at y≈9040-10434px in a 963px window: in the DOM,
  // visible, opacity 1, just ~9000px below the fold. The first pan called
  // applyViewport and everything snapped in — the "canvas items don't appear
  // until I pan" report (per Nika, "it's a rendering issue and you're not
  // fixing the right thing"; he was right). Idempotent: applyViewport is a
  // pure write of transform + grid + screen-space chrome from the camera it's
  // handed, so re-running it with the live camera changes nothing.
  if (board?.viewport && typeof board.viewport.scale === 'number') {
    this.call('applyViewport', board.viewport)
  }

  if (Array.isArray(board?.pages) && board.pages.length) {
    if (Object.keys(board.items || {}).length) this.call('syncCanvasItemsFromPages')
    this.call('subscribeMarketplaceInstalls')
    return
  }

  const ctx = this.context || this.getRootContext?.() || {}
  const pages = ctx.projectPages || {}
  const components = root?.data?.components || {}
  // The two flags read the live board first. A board of another project holds
  // no flag of this one: read this project's saved flags.
  const flagsFrom = board ? root : { data: root?.data }
  const showDesignSystem = shouldShowDesignSystem(flagsFrom)
  const showComponents = shouldShowComponents(flagsFrom)
  // Restore previously persisted layout from the top-level `canvas`
  // field, with fallbacks for older saves. Only honour it if it
  // actually has items — a bare `canvas: {}` falls through to seed.
  const persistedRaw =
    root?.data?.canvas || root?.data?.canvasLayout || root?.data?.settings?.canvas
  // Honour a persisted doc that carries BOARD STATE, not just one with tiles.
  // Gating this on `items` alone silently discarded every added page on reload:
  // `pages`/`activePageId` persist fine, but a board whose ACTIVE page has no
  // tiles read as "never initialised" and fell through to the fresh-seed path
  // below, which writes `...DEFAULTS()` — and DEFAULTS carries the single
  // `page-1`. That's the real cause of the "add-page reverts" symptom: a
  // just-added page is empty by construction, and since #203 made the
  // design-system + component frames default OFF, an empty active page is the
  // common case rather than a corner. A legacy pre-pages doc has no `pages`
  // key, so it keeps the old items-only behaviour exactly. (Nika 2026-08-02.)
  const hasPersistedBoard =
    !!persistedRaw &&
    (Object.keys(persistedRaw.items || {}).length > 0 ||
      (Array.isArray(persistedRaw.pages) && persistedRaw.pages.length > 0))
  const persisted = hasPersistedBoard ? persistedRaw : null
  // Seed the device-preview default on this project's first open, so a mobile
  // project does not open on whatever breakpoint a previously-viewed project
  // left active. Runs once per project per tab-session, from every branch
  // below that can own a fresh load. previewSize has no persisted slot of its
  // own (a session cookie plus the canvas scope), so this mirrors the same
  // pair DeviceButtonSet writes.
  //
  // TWO sources, in this order:
  //
  //   1. the persisted board's own `previewSize`, for a project that has one;
  //   2. the PROJECT TYPE, which is the one that actually fires on a fresh
  //      create.
  //
  // Source 2 is not a fallback for tidiness — without it this seed was dead
  // code on the path it was written for. `persistedRaw` reads
  // `root.data.canvas`, and `root.data` on the canvas route is the PROJECT
  // RECORD: measured live 2026-09-04 on a just-created mobile project
  // (workspace.localhost:1355/nikoloza/ws-mobile-live-check-4), `root.data`
  // carries forkedFrom/_id/name/key/projectType/environments/… and NO
  // `canvas` key at all, so `persisted` was null and this returned on its
  // first line every time — no cookie, no scope write. The tiles still landed
  // in the right places, which is what hid it: autoLayout derives the same
  // positions from the page list, so the board looked correct while the
  // breakpoint leg silently did nothing.
  //
  // The project type is the honest source anyway. A `mobile` project should
  // preview at a mobile breakpoint because of what it IS, not because one
  // template remembered to say so — and `projectType` is on the record every
  // consumer already reads. `mobileXS` is the named 375x768 size in the
  // DeviceButtonSet vocabulary. Only `mobile` maps: app/service/deck keep
  // today's unforced default, exactly as the spec asks.
  const seedPreviewSizeOnce = () => {
    if (!urlKey) return
    const size = persisted?.previewSize || PREVIEW_SIZE_BY_PROJECT_TYPE[root?.data?.projectType]
    if (!size) return
    const seedKey = `${urlOwner}/${urlKey}`
    if (previewSizeSeededProjects.has(seedKey)) return
    previewSizeSeededProjects.add(seedKey)
    this.call('setCookie', 'previewSize', size)
    const canvasScope = this.call('getCanvasScope')
    if (canvasScope) canvasScope.previewSize = size
  }
  if (persisted && root?.update && !Object.keys(board?.items || {}).length) {
    const stored = loadStored()
    const base = DEFAULTS()
    if (stored.snap) base.snap = { ...base.snap, ...stored.snap }
    const initialFocus = persisted.initialFocus
    const shouldFocus = !!(initialFocus && persisted.items?.[initialFocus])
    const items = Object.fromEntries(
      Object.entries(persisted.items || {}).filter(([key, item]) => {
        if (!showDesignSystem && isDsItemKey(key)) return false
        // Only prune items explicitly marked as auto-seeded component-showcase
        // frames (`_componentShowcase`, stamped by the gallery-seed paths
        // below and in syncCanvasItemsFromPages) — NOT every item whose key
        // happens to match a project component. A component can also BE a
        // project's sole board/page tile: the Blank template's only content
        // is `components: { home }` with no pages, and its `home` tile must
        // never be pruned just because "home" is also a component name.
        if (!showComponents && item?._componentShowcase) return false
        return true
      })
    )
    root.update({
      canvas: {
        ...base,
        // Board pages: honour a persisted pages list, else migrate this legacy
        // single-board doc — its `items` below become the first page (Nika
        // 2026-08-01), named in the session language. The name resolves only
        // for a doc with no pages; `base`'s own page is replaced here either way.
        ...migrateBoardPages(persisted, () => boardPageDefaultName(this, 1)),
        items,
        guides: persisted.guides || base.guides,
        groups: persisted.groups || {},
        showDesignSystem,
        showComponents,
        selection: shouldFocus ? { ids: [initialFocus], primary: initialFocus } : base.selection,
        // A persisted `{}` viewport is truthy but has no scale → the screen-space
        // chrome (selection box, SelectedNavbar) computes NaN positions and
        // floats off the items. Only honour a persisted viewport that actually
        // carries a scale; otherwise fall back to the default. (2026-06-17.)
        viewport:
          persisted.viewport && persisted.viewport.scale != null
            ? persisted.viewport
            : base.viewport,
        projectId: root.data._id
      }
    })
    // Paint the restored camera. On a first open the scene's mount poll
    // (CanvasSurface CvScene) paints it too, but on a project switch that poll
    // has already stopped on the last board's camera.
    this.call('applyViewport', root.canvas.viewport)
    seedPreviewSizeOnce()
    if (shouldFocus) {
      const focus = () => {
        this.call('fitToSelection')
        // The persisted rewrite intentionally omits `initialFocus`, making this
        // a first-open behavior rather than resetting the user's viewport later.
        this.call('persistCanvasLayout')
      }
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(focus)
      else setTimeout(focus, 0)
    }
    this.call('pushHistory')
    this.call('subscribeMarketplaceInstalls')
    return
  }
  if (!root?.update) return
  if (board && Object.keys(board.items || {}).length) {
    // Heal a viewport that was persisted/left as `{}` (no scale) — otherwise the
    // screen-space chrome positions at NaN and the selection box / SelectedNavbar
    // float off the items. (2026-06-17.)
    if (!root.canvas.viewport || root.canvas.viewport.scale == null) {
      this.call('applyViewport', { x: 40, y: 40, scale: 0.35 })
    } else {
      // …and APPLY a valid restored one. Restoring the persisted camera into
      // STATE (the `root.update({ …viewport })` above) never touched the DOM:
      // CvScene keeps its declared placeholder transform
      // `translate3d(40px, 40px, 0) scale(0.35)` until something calls
      // applyViewport, so a board saved at any other camera painted every tile
      // at the WRONG place — measured 2026-08-08 on natali/shop: state camera
      // {x: 702, y: -6192, scale: 0.2308} while the scene still read the 40/40/
      // 0.35 default, putting all six mounted tiles at y≈9040-10434px in a
      // 963px window (in the DOM, visible, opacity 1 — just ~9000px below the
      // fold). The first pan called applyViewport and everything snapped in,
      // which is exactly the "canvas items don't appear until I pan" report
      // (per Nika: "it's a rendering issue and you're not fixing the right
      // thing" — he was right). Also re-syncs the dot grid + screen-space
      // chrome, which were stale for the same reason.
      this.call('applyViewport', root.canvas.viewport)
    }
    // Reconcile so canvases created before pages/components/design-system frames
    // shipped pick them up (no-op when nothing's missing).
    this.call('syncCanvasItemsFromPages')
    // A template that ships its own real canvas.items (mobile) lands HERE on
    // first open, not in the persisted-restore branch above — its items are
    // never empty, so that branch's `!items.length` gate never lets it in.
    seedPreviewSizeOnce()
    this.call('subscribeMarketplaceInstalls')
    return
  }

  // Also seed the project's COMPONENTS as frames — a CanvasItem renders ANY
  // project key through embedComponent, not just pages, so a component-only
  // project (pages: {}, components: { … }) is no longer blank. (2026-06-17 per
  // Nika "also show components on project canvas".)
  // Built-in design-system pages — appended so they sit after the user's own
  // pages/components in the grid. Each renders the user's design system.
  const dsEntries = showDesignSystem ? DS_ITEM_KEYS.map((k) => [k, { _ds: true }]) : []
  const componentEntries = showComponents ? Object.entries(components) : []
  const entries = [...Object.entries(pages), ...componentEntries, ...dsEntries]
  // Keys added ONLY via the opt-in component-gallery seed above (not a page
  // that happens to share a key with a component). Stamped onto the item
  // below so a future prune (showComponents toggled off, here or in
  // syncCanvasItemsFromPages) can tell a showcase frame apart from a
  // component that's also the project's actual page/board content.
  const pageKeySet = new Set(Object.keys(pages))
  const componentShowcaseKeys = new Set(
    componentEntries.map(([key]) => key).filter((key) => !pageKeySet.has(key))
  )

  // 4-col layout at 1722×1722 canvas-space px per thumbnail with 256 gap.
  const W = 1722
  const GAP = 256
  const COLS = 4
  const items = {}
  entries.forEach(([key, v], i) => {
    const col = i % COLS
    const row = Math.floor(i / COLS)
    const saved = v?.settings?.gridOptions || {}
    const usingSaved = saved.x != null && saved.y != null
    items[key] = {
      x: usingSaved ? (saved.x * (W + GAP)) / 3 : col * (W + GAP),
      y: usingSaved ? (saved.y * (W + GAP)) / 3 : row * (W + GAP),
      w: usingSaved ? (saved.w || 3) * (W / 3) : W,
      h: usingSaved ? (saved.h || 3) * (W / 3) : W,
      pageKey: key,
      ...(componentShowcaseKeys.has(key) ? { _componentShowcase: true } : {})
    }
  })

  // Restore persisted snap + ruler/minimap visibility settings. A fresh board's
  // first page takes its name in the session language.
  const stored = loadStored()
  const base = DEFAULTS(boardPageDefaultName(this, 1))
  if (stored.snap) base.snap = { ...base.snap, ...stored.snap }
  if (typeof stored.showRulers === 'boolean') base.showRulers = stored.showRulers
  if (typeof stored.showMinimap === 'boolean') base.showMinimap = stored.showMinimap
  root.update({
    canvas: { ...base, items, showDesignSystem, showComponents, projectId: root.data?._id }
  })
  // Paint the seeded camera (a project switch: see the restore branch above).
  // fitCanvasToContent below re-paints it when there is content to fit.
  this.call('applyViewport', root.canvas.viewport)
  // THE branch a freshly created project actually takes. The two calls above
  // both sit behind a gate a new project fails — one needs a persisted board
  // (`root.data.canvas`, which the canvas route never carries), the other
  // needs `root.canvas.items` already populated — so before this call the
  // seed could not run on a create at all. Measured live 2026-09-04: a new
  // mobile project opened with no previewSize cookie and no scope write.
  seedPreviewSizeOnce()
  // Zoom to fit — ONLY on this fresh-seed path (tickets/nika.md "PRODUCT CALL",
  // 2026-08-16). Reached only when `hasPersistedBoard` was false, i.e. the
  // project has no saved arrangement at all: no items AND no pages. A board
  // with a saved camera returns from the `persisted` branch far above and
  // never arrives here, so its viewport is untouched by construction — that
  // is the regression this must not cause, and the control flow, not a
  // conditional here, is what prevents it.
  //
  // Ordered BEFORE pushHistory so the fitted camera is the first history
  // entry rather than an immediate undo target — otherwise a user's first
  // ctrl-Z would throw them back to the unfitted 0.35 default.
  //
  // Safe no-op if CanvasSurface has no rect yet or there are no items: the
  // `root.update` above already seeded a valid fallback camera synchronously,
  // so doing nothing leaves a working board, never a blank one.
  this.call('fitCanvasToContent')
  this.call('pushHistory')
  this.call('subscribeMarketplaceInstalls')
}

// ── Marketplace-install subscription (#2048, modal-install half) ──
//
// `initCanvasV2` seeds v2.items once from context.projectPages and
// never re-syncs.  When a user installs a template/library via the
// modal flow (not the drag-drop flow), the server creates a new
// project page and the marketplace broadcasts a postMessage telling
// the canvas tab to sync.  Drag-drop installs already patch v2.items
// directly via installMarketplaceItem — this subscription only covers
// the modal/button-install path that the drag-drop doesn't touch.
//
// Self-guarded: a top-level flag prevents duplicate listeners across
// HMR reloads, and the listener no-ops when the canvas root isn't
// mounted.
let _marketplaceInstallSubscribed = false
let _canvasOwner = null
export function subscribeMarketplaceInstalls() {
  if (typeof window === 'undefined') return
  _canvasOwner = this
  if (_marketplaceInstallSubscribed) return
  _marketplaceInstallSubscribed = true
  window.addEventListener('message', (ev) => {
    if (ev?.data?.type !== 'symbols:marketplace-installed') return
    // a first-party sender only — a user-code tile on this page cannot spam it
    if (!isTrustedInstallMessage(ev)) return
    const owner = _canvasOwner
    if (!owner) return
    // Small delay so the server-side mutation lands in
    // context.projectPages before we re-scan it.  600ms mirrors the
    // existing `symbols:integration-installed` listener in
    // canvas/pages/studios.js.
    setTimeout(() => {
      try {
        owner.call?.('syncCanvasItemsFromPages')
      } catch (err) {
        console.warn('[canvas v2] post-install sync failed:', err?.message || err)
      }
    }, 600)
  })
}

// Every item key that exists ANYWHERE on the board — the active page's live
// `items` plus every parked page's `pageItems[id].items` snapshot (board
// pages, Nika 2026-08-01). A key absent from the ACTIVE page alone is not
// necessarily new: it may simply be divided onto another page.
// syncCanvasItemsFromPages used to check `v2.items` alone, so it re-added
// every parked key onto whichever page happened to be open on the very next
// toggle / add-page / remote-op — silently re-merging a deliberately divided
// board back into one page. (applyRemoteOps.js's `removeTile` had the
// mirror-image deletion bug — fixed there too.)
const _allBoardItemKeys = (v2) => {
  const keys = new Set(Object.keys(v2.items || {}))
  const pageItems = v2.pageItems || {}
  for (const id in pageItems) {
    const parked = pageItems[id]?.items
    if (!parked) continue
    for (const key in parked) keys.add(key)
  }
  return keys
}

// Reconcile v2.items against the current context.projectPages.  Adds
// entries for pages that landed after init (e.g. via marketplace
// install or any other mutation that writes data.pages[newKey]) and are
// missing from EVERY board page — not just the active one, so a project
// already divided across pages doesn't get silently re-merged back onto
// whichever page happens to be open (board pages, Nika 2026-08-01).
// Does NOT remove existing items — drag-drop tiles whose install is
// still in flight live in v2.items before the page lands.  Page
// removals are still a manual action via deleteSelection.
export function syncCanvasItemsFromPages() {
  const v2 = this.call('getCanvasV2')
  if (!v2) return
  const ctx = this.context || this.getRootContext?.() || {}
  const pages = ctx.projectPages || {}
  const root = rootState(this)
  const components = root?.data?.components || {}
  const showDesignSystem = shouldShowDesignSystem(root)
  const showComponents = shouldShowComponents(root)
  const existingKeys = _allBoardItemKeys(v2)
  const W = 1722
  const GAP = 256
  const COLS = 4
  const next = { ...v2.items }
  let changed = 0
  if (!showDesignSystem) {
    for (const key of Object.keys(next)) {
      if (!isDsItemKey(key)) continue
      delete next[key]
      changed++
    }
  }
  // Prune component-showcase frames when components are toggled off (mirrors
  // the DS prune above). Only items explicitly marked `_componentShowcase`
  // (stamped by the gallery-seed paths — this function's own reconcile block
  // below, and initCanvasV2's fresh seed) are eligible — NOT every item whose
  // key happens to match a project component. A component can also BE a
  // project's sole board/page tile (e.g. the Blank template, whose only
  // content is `components: { home }` with no pages), and must never be
  // pruned just because its key is a component name.
  if (!showComponents) {
    for (const key of Object.keys(next)) {
      if (!next[key]?._componentShowcase) continue
      delete next[key]
      changed++
    }
  }
  // Find the next available grid slot by scanning existing positions
  // — keeps new tiles roughly in-grid with the seed layout.
  const usedSlots = new Set()
  for (const it of Object.values(v2.items || {})) {
    if (typeof it.x !== 'number' || typeof it.y !== 'number') continue
    const col = Math.round(it.x / (W + GAP))
    const row = Math.round(it.y / (W + GAP))
    if (col >= 0 && row >= 0) usedSlots.add(`${col},${row}`)
  }
  let probeCol = 0
  let probeRow = 0
  const nextFreeSlot = () => {
    while (usedSlots.has(`${probeCol},${probeRow}`)) {
      probeCol++
      if (probeCol >= COLS) {
        probeCol = 0
        probeRow++
      }
    }
    const slot = { col: probeCol, row: probeRow }
    usedSlots.add(`${probeCol},${probeRow}`)
    probeCol++
    if (probeCol >= COLS) {
      probeCol = 0
      probeRow++
    }
    return slot
  }
  for (const [key, value] of Object.entries(pages)) {
    if (existingKeys.has(key)) continue
    // Honour any saved gridOptions the page already carries.  Mirrors
    // initCanvasV2's seeding math so resync tiles align with the
    // existing layout.
    const saved = value?.settings?.gridOptions || {}
    const usingSaved = saved.x != null && saved.y != null
    let x, y, w, h
    if (usingSaved) {
      x = (saved.x * (W + GAP)) / 3
      y = (saved.y * (W + GAP)) / 3
      w = (saved.w || 3) * (W / 3)
      h = (saved.h || 3) * (W / 3)
    } else {
      const { col, row } = nextFreeSlot()
      x = col * (W + GAP)
      y = row * (W + GAP)
      w = W
      h = W
    }
    next[key] = { x, y, w, h, pageKey: key }
    changed++
  }
  // Also reconcile the project's COMPONENTS as frames (mirrors the seed in
  // initCanvasV2) so a component added after init shows up too (2026-06-17) —
  // only when components are toggled on (default off, Nika 2026-08-01).
  if (showComponents) {
    for (const key of Object.keys(components)) {
      if (existingKeys.has(key) || next[key]) continue
      const { col, row } = nextFreeSlot()
      next[key] = {
        x: col * (W + GAP),
        y: row * (W + GAP),
        w: W,
        h: W,
        pageKey: key,
        _componentShowcase: true
      }
      changed++
    }
  }
  // Built-in design-system pages (mirrors the seed) — so they appear on canvases
  // created before this shipped, too.
  if (showDesignSystem) {
    for (const key of DS_ITEM_KEYS) {
      if (existingKeys.has(key) || next[key]) continue
      const { col, row } = nextFreeSlot()
      next[key] = { x: col * (W + GAP), y: row * (W + GAP), w: W, h: W, pageKey: key }
      changed++
    }
  }
  if (!changed) return
  this.call('patchCanvas', { items: next })
  this.call('refreshCanvasChrome')
  this.call('persistCanvasLayout')
}

// Toggle the design-system frames on/off for this board (default off, Nika
// 2026-08-01). Flips the live flag, then re-runs the reconcile which
// seeds (when on) or prunes (when off) the DS tiles + refreshes chrome +
// persists. The explicit persist covers the edge where the reconcile makes no
// item change (e.g. toggling on a board that already had the tiles).
export function toggleCanvasDesignSystem() {
  const root = rootState(this)
  this.call('patchCanvas', { showDesignSystem: !shouldShowDesignSystem(root) })
  this.call('syncCanvasItemsFromPages')
  this.call('persistCanvasLayout')
}

// Toggle the component frames on/off for this board (default off, Nika
// 2026-08-01). Same shape as toggleCanvasDesignSystem.
export function toggleCanvasComponents() {
  const root = rootState(this)
  this.call('patchCanvas', { showComponents: !shouldShowComponents(root) })
  this.call('syncCanvasItemsFromPages')
  this.call('persistCanvasLayout')
}

// ── Board pages (Figma-style) — Nika 2026-08-01 ─────────────────────────────
// The ACTIVE page's layout lives in the top-level live canvas fields; inactive
// pages are parked as snapshots in `canvas.pageItems[id]`. Switching swaps the
// two. A board always keeps ≥1 page.

// The live-field slice that makes up one page's layout.
const _livePageSlice = (v2) => ({
  items: v2.items || {},
  guides: v2.guides || DEFAULTS().guides,
  groups: v2.groups || {},
  viewport: v2.viewport || DEFAULTS().viewport,
  connections: v2.connections || []
})

// Ensure a loaded canvas doc carries the board-pages shape. A legacy single-board
// doc (no `pages`) migrates transparently — its existing live items become the
// content of the first page. Pure; returns the fields to merge onto canvas.
// `firstPageName` is a function that answers that page's name
// (boardPageDefaultName: the session language). It is asked ONLY for a doc with
// no pages — a doc that has them keeps its saved names and resolves nothing.
// Without it the first page is named by its number.
export function migrateBoardPages(canvas, firstPageName) {
  if (Array.isArray(canvas?.pages) && canvas.pages.length) {
    return {
      pages: canvas.pages,
      activePageId:
        canvas.activePageId && canvas.pages.some((p) => p.id === canvas.activePageId)
          ? canvas.activePageId
          : canvas.pages[0].id,
      pageItems: canvas.pageItems || {}
    }
  }
  const name = (typeof firstPageName === 'function' && firstPageName()) || '1'
  return {
    pages: [{ id: 'page-1', name }],
    activePageId: 'page-1',
    pageItems: canvas?.pageItems || {}
  }
}

// el.call('switchBoardPage', id) — park the current page, activate another.
export function switchBoardPage(id) {
  const root = rootState(this)
  const v2 = root?.canvas
  if (!v2 || !id || id === v2.activePageId) return
  const pages = Array.isArray(v2.pages) ? v2.pages : []
  if (!pages.some((p) => p.id === id)) return
  const pageItems = { ...(v2.pageItems || {}) }
  // Snapshot the outgoing page's live layout.
  if (v2.activePageId) pageItems[v2.activePageId] = _livePageSlice(v2)
  // Hydrate the incoming page (empty defaults for a never-visited page).
  const snap = pageItems[id] || {}
  this.call('patchCanvas', {
    activePageId: id,
    pageItems,
    items: snap.items || {},
    guides: snap.guides || DEFAULTS().guides,
    groups: snap.groups || {},
    viewport: snap.viewport || DEFAULTS().viewport,
    connections: snap.connections || [],
    selection: { ids: [], primary: null }
  })
  this.call('refreshCanvasChrome')
  this.call('persistCanvasLayout')
}

// el.call('addBoardPage', name?) — create a new empty page and switch to it.
// With no name, the page takes the default name in the session language.
export function addBoardPage(name) {
  const root = rootState(this)
  const v2 = root?.canvas
  if (!v2) return
  const pages = Array.isArray(v2.pages) ? v2.pages : []
  const existing = new Set(pages.map((p) => p.id))
  let n = pages.length + 1
  let id = `page-${n}`
  while (existing.has(id)) {
    n++
    id = `page-${n}`
  }
  const pageName = (name && String(name).trim()) || boardPageDefaultName(this, pages.length + 1)
  // Add the page, snapshot the current one, and activate the new empty page — all
  // in ONE patch. Successive patchCanvas calls read stale state under the signal
  // store's batched update, so a separate switchBoardPage would see pages without
  // the just-added entry and bail.
  const pageItems = { ...(v2.pageItems || {}) }
  if (v2.activePageId) pageItems[v2.activePageId] = _livePageSlice(v2)
  this.call('patchCanvas', {
    pages: [...pages, { id, name: pageName }],
    pageItems,
    activePageId: id,
    items: {},
    guides: DEFAULTS().guides,
    groups: {},
    viewport: DEFAULTS().viewport,
    connections: [],
    selection: { ids: [], primary: null }
  })
  this.call('refreshCanvasChrome')
  this.call('persistCanvasLayout')
}

// el.call('renameBoardPage', id, name)
export function renameBoardPage(id, name) {
  const root = rootState(this)
  const v2 = root?.canvas
  if (!v2 || !id) return
  const trimmed = (name && String(name).trim()) || ''
  if (!trimmed) return
  const pages = (Array.isArray(v2.pages) ? v2.pages : []).map((p) =>
    p.id === id ? { ...p, name: trimmed } : p
  )
  this.call('patchCanvas', { pages })
  this.call('persistCanvasLayout')
}

// ── Board-page META bag (spec §3.7) ────────────────────────────────────────
// A plain, OPAQUE bag persisted inside `canvas.pages[i].meta`. The canvas
// defines no field in it: a workspace module writes its own keys (VaultOS
// stores `trainingRecordRef`, linking one board page to one training record)
// and reads them back. It rides the existing `pages` array, so it persists,
// migrates (migrateBoardPages passes `pages` through untouched) and travels
// with version history for free — no new content field, no new endpoint.

// el.call('getBoardPageMeta', pageId?) → the bag (never null). Omit `pageId`
// for the ACTIVE page, which is what every caller wants.
export function getBoardPageMeta(pageId) {
  const v2 = this.call('getCanvasV2')
  if (!v2) return {}
  const id = pageId || v2.activePageId
  const page = (Array.isArray(v2.pages) ? v2.pages : []).find((p) => p.id === id)
  return (page && page.meta) || {}
}

// el.call('setBoardPageMeta', pageId, patch) — MERGES into the bag; a key set
// to `undefined` is dropped, which is how a module removes one. No-ops on an
// unknown page id rather than minting a page that does not exist.
export function setBoardPageMeta(pageId, patch) {
  const v2 = this.call('getCanvasV2')
  if (!v2 || !patch) return false
  const id = pageId || v2.activePageId
  const pages = Array.isArray(v2.pages) ? v2.pages : []
  if (!pages.some((p) => p.id === id)) return false
  const next = pages.map((p) => {
    if (p.id !== id) return p
    const meta = { ...(p.meta || {}), ...patch }
    for (const k of Object.keys(meta)) if (meta[k] === undefined) delete meta[k]
    return { ...p, meta }
  })
  this.call('patchCanvas', { pages: next })
  this.call('persistCanvasLayout')
  return true
}

// el.call('deleteBoardPage', id) — never leaves the board with 0 pages; if the
// active page is deleted, falls back to the first remaining page.
export function deleteBoardPage(id) {
  const root = rootState(this)
  const v2 = root?.canvas
  if (!v2 || !id) return
  const pages = Array.isArray(v2.pages) ? v2.pages : []
  if (pages.length <= 1) return
  const remaining = pages.filter((p) => p.id !== id)
  if (remaining.length === pages.length) return
  const pageItems = { ...(v2.pageItems || {}) }
  delete pageItems[id]
  if (v2.activePageId === id) {
    // Deleting the active page: switch to the first remaining, then drop it.
    const fallback = remaining[0].id
    const snap = pageItems[fallback] || {}
    this.call('patchCanvas', {
      pages: remaining,
      pageItems,
      activePageId: fallback,
      items: snap.items || {},
      guides: snap.guides || DEFAULTS().guides,
      groups: snap.groups || {},
      viewport: snap.viewport || DEFAULTS().viewport,
      connections: snap.connections || [],
      selection: { ids: [], primary: null }
    })
    this.call('refreshCanvasChrome')
  } else {
    this.call('patchCanvas', { pages: remaining, pageItems })
  }
  this.call('persistCanvasLayout')
}
